import { NgFor, NgIf } from "@angular/common";
import { afterNextRender, ChangeDetectionStrategy, Component, ElementRef, Input, ViewChild } from "@angular/core";
import { Router, RouterModule } from "@angular/router";
import KeenSlider, { KeenSliderInstance, KeenSliderPlugin } from "keen-slider";

@Component({
  selector: "app-slider-gallery",
  imports: [NgIf, NgFor, RouterModule],
  templateUrl: "./slider-gallery.component.html",
  styleUrl: "./slider-gallery.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Componente que muestra un pequeño slider de miniaturas de imagenes.
 */
export class SliderGalleryComponent {
  @Input() images: any[] = [];
  @Input() genericAlt: string = "";
  @Input() isVideo: boolean = false;

  private _parent;
  @Input() set parent(parent) {
    this._parent = parent;
    if (this.thumbnailSlider) {
      this.reinitSlider();
    }
  }

  get parent() {
    return this._parent;
  }

  /**
   * Galeria a la que pertenece el slider de miniaturas.
   */
  private _gallery;
  @Input() set gallery(gallery) {
    this._gallery = gallery;
    if (this.thumbnailSlider) {
      this.reinitSlider();
    }
  }

  get gallery() {
    return this._gallery;
  }

  thumbnailSlider: KeenSliderInstance = null;

  @ViewChild("thumbnailRef") thumbnailRef: ElementRef<HTMLElement>;

  constructor(private router: Router) {
    afterNextRender(() => {
      this.reinitSlider();
    });
  }

  /**
   * Reinicia el slider de miniaturas.
   * @param {number} slideNumber - Número de slides a mostrar.
   */
  reinitSlider(slideNumber: number = 5) {
    if (this.thumbnailSlider) {
      this.thumbnailSlider.slides.forEach((slide) => {
        slide.classList.remove("active");
      });
    }
    this.thumbnailSlider = new KeenSlider(
      this.thumbnailRef.nativeElement,
      {
        initial: 0,
        slides: {
          perView: slideNumber,
        },
      },
      [this.thumbnailPlugin(this.images, this.router, this._parent, this._gallery)],
    );
  }

  /**
   * Inicializa el plugin del slider de miniaturas para que al hacer click en una miniatura se muestre la imagen correspondiente en el slider principal.
   * @param images - Colección de imagenes.
   * @param router - Router de la aplicación.
   * @param main - Slider principal.
   * @param gallery - Galería a la que pertenece el slider de miniaturas.
   * @returns
   */
  thumbnailPlugin(images, router, main?: KeenSliderInstance, gallery?): KeenSliderPlugin {
    return (slider) => {
      function removeActive() {
        slider.slides.forEach((slide) => {
          slide.classList.remove("active");
        });
      }
      function addActive(idx: number) {
        slider.slides[idx].classList.add("active");
      }

      function addClickEvents() {
        slider.slides.forEach((slide, idx) => {
          slide.addEventListener("click", () => {
            if (main) {
              main.moveToIdx(idx);
            } else if (gallery) {
              router.navigate([this.isVideo === true ? "/videos" : "/albums", images[idx].id]);
              removeActive();
              addActive(idx);
              slider.moveToIdx(idx);
              gallery.reset();
            }
          });
        });
      }

      slider.on("created", () => {
        addActive(slider.track.details.rel);
        addClickEvents();
        if (main) {
          main.on("animationStarted", (main) => {
            removeActive();
            const next = main.animator.targetIdx || 0;
            addActive(main.track.absToRel(next));
            slider.moveToIdx(Math.min(slider.track.details.maxIdx, next));
          });
        }
      });
    };
  }

  trackByItem(index: number, item: any) {
    return item;
  }
}
